import React, { useContext, useState, useEffect, useRef } from "react";
import Uri from "jsuri";

import AbortController from "abort-controller";

import { BASEURL, HEADERS } from "./../Utils/Constants";
import { AppContext } from "./../../contexts/AppContextProvider";

import { QueryBuilderContext } from "../../contexts/QueryBuilderContextProvider";
import { hasMetrics } from "./Utils/QueryBuilderUtils";

import { QueryBuilderInsightsFeedLoader } from "./Elements/QueryLoaders";

import QueryInsightsFeedList from "./Insights/QueryInsightsFeedList";

import { Error } from "./../Utils/Utils.js";

import pickaxe_insights_logo from "./../../i/icons/pickaxe_insights_logo.svg";

let abortController = new AbortController();

const QueryInsightsFeed = (props) => {
  const { state: queryBuilderState, dispatch } =
    useContext(QueryBuilderContext);
  const { state: appState } = useContext(AppContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [insights, setInsights] = useState([]);
  const [firstRender, setFirstRender] = useState(true);

  const [page, setPage] = useState(0);
  const [moreData, setMoreData] = useState(true);

  const insightsFeedRef = useRef(null);

  const fetchData = async () => {
    try {
      abortController.abort();
      abortController = new AbortController();

      setLoading(true);
      dispatch({
        type: "SET-INSIGHTS-FEED-LOADING",
        payload: true,
      });

      // TODO: This is a bug and needs to be removed
      if (!appState.currentProject.projectObj.insightsConfig.queryBuilder)
        return null;

      let insightsConfigURL = new Uri(
        appState.currentProject.projectObj.insightsConfig.queryBuilder.url
      );

      insightsConfigURL.addQueryParam("project_id", props.projectId);

      // URL overwrites
      const thresholdParam = new Uri(window.location.search).getQueryParamValue(
        "threshold"
      );

      const versionParam = localStorage.configVersion || "production";

      if (thresholdParam) {
        insightsConfigURL = insightsConfigURL.replaceQueryParam(
          "threshold",
          thresholdParam
        );
      }

      if (versionParam) {
        insightsConfigURL = insightsConfigURL.replaceQueryParam(
          "version",
          versionParam
        );
      }

      const insightsParams = {
        threshold:
          thresholdParam || insightsConfigURL.getQueryParamValue("threshold"),
        paging: {
          page: page,
          count: 8 || insightsConfigURL.getQueryParamValue("count"),
        },
      };

      const queryParams = JSON.stringify({
        ...queryBuilderState.query,
        ...insightsParams,
      });

      // const endpoint = `/dashboard/${tenantSlug}/query-insights?project_id=${props.projectId}&version=${version}&query=`;

      const url = `${BASEURL}${insightsConfigURL}&query=${queryParams}`;

      let response = await fetch(url, {
        method: "GET",
        headers: HEADERS(window.localStorage.token),
        signal: abortController.signal,
      });

      const handleError = () => {
        setLoading(false);
        setError(true);
        dispatch({
          type: "SET-INSIGHTS-FEED-LOADING",
          payload: false,
        });
      };

      if (!response.ok || response.error) {
        handleError();
      } else {
        let data = await response.json();
        // in the case that some exception was returned as message
        if (data.error) {
          handleError();
          return;
        }

        // setInsights([...insights, ...data.insights]);
        setInsights((insights) => insights.concat(data.insights));

        // msgs => msgs.concat(JSON.parse(msg.data)));

        if (insights.length > 0) {
          setPage((page) => page + 1);
        }

        setLoading(false);
        setError(false);
        setMoreData(data.moreData);
        setFirstRender(false);

        dispatch({
          type: "SET-INSIGHTS-FEED-LOADING",
          payload: false,
        });

        dispatch({
          type: "SET-INSIGHTS-FEED",
          payload: data.insights,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (hasMetrics(queryBuilderState)) {
      // Reset Insights
      setInsights([]);
      setPage(0);
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryBuilderState.query]);

  const renderInsights = () => {
    if (loading || firstRender) {
      return <QueryBuilderInsightsFeedLoader />;
    } else if (insights && insights.length > 0) {
      return insights.map((item, i) =>
        item.data.map((insightsData, i) => (
          // <InsightArchive key={i} {...insightsData} text_size={4} />
          <QueryInsightsFeedList key={i} {...insightsData} text_size={4} />
        ))
      );
    }
  };

  const fetchMoreInsights = () => {
    if (error || loading || !moreData) {
      return null;
    } else {
      fetchData();
    }
  };

  const renderMoreInsights = () => {
    if (error) {
      return (
        <div className="d-flex flex-column justify-content-center align-items-center text-center h-50">
          <Error minHeight={360} type="query_feed_insights" />
        </div>
      );
    }

    if (loading && moreData) {
      return <QueryBuilderInsightsFeedLoader />;
    } else {
      if (insights && insights.length && moreData) {
        return (
          <div className="d-flex justify-content-left">
            <button
              className="btn btn-primary"
              onClick={() => fetchMoreInsights()}
            >
              View More{" "}
            </button>
          </div>
        );
      }
    }
  };

  const renderTextQueryOrLoader = () => {
    if (!firstRender) {
      if (insights && insights.length > 0 && !loading) {
        return (
          queryBuilderState &&
          queryBuilderState.queryTextVersion.replace("Show me", "For")
        );
      } else if (insights && insights.length === 0 && !loading) {
        return (
          <span>
            We haven’t uncovered anything interesting about this query yet.
          </span>
        );
      }
    } else {
      return null;
    }
  };

  return (
    <div className="row" ref={insightsFeedRef}>
      <div className="col-10 mt-5 pl-4 pr-3">
        <div className="d-flex justify-content-left align-items-center mb-3">
          <span className={"query-insights-label"}>
            <img src={pickaxe_insights_logo} alt="Insights" className="mr-1" />
            Insights
          </span>

          <span className="query-insights-label-query ml-1 animated fadeIn">
            {renderTextQueryOrLoader()}
          </span>
        </div>
        {renderInsights()}
        {renderMoreInsights()}
      </div>
    </div>
  );
};

export default React.memo(QueryInsightsFeed);
